import { useState, useEffect } from "react";
import tmdbApi from "../../api/tmdbApi";

// IST date format used across shared links list and detail pages
export const formatShareDate = (value) => {
    if (!value) return "";
    const dateObj = new Date(value);

    const day = dateObj.toLocaleString('en-GB', { day: '2-digit', timeZone: 'Asia/Kolkata' });
    const month = dateObj.toLocaleString('en-GB', { month: '2-digit', timeZone: 'Asia/Kolkata' });
    const year = dateObj.toLocaleString('en-GB', { year: 'numeric', timeZone: 'Asia/Kolkata' });
    const time = dateObj.toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata'
    });

    return `${day}-${month}-${year} ${time}`.toUpperCase();
};

export const getShareStatus = (row) => row?.isExpired ? "Expired" : "Active";

const formatDuration = (ms) => {
    const totalMins = Math.floor(ms / 60000);
    const days = Math.floor(totalMins / 1440);
    const hrs = Math.floor((totalMins % 1440) / 60);
    const mins = totalMins % 60;
    if (days > 0) return hrs > 0 ? `${days}d ${hrs}h` : `${days}d`;
    if (hrs > 0) return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
    if (mins > 0) return `${mins}m`;
    return "< 1m";
};

// time left on a link: expiry = updated + hours (falls back to expiresAt).
// "expired" still comes only from the api's isExpired flag.
export const getTimeLeft = (row, now = Date.now()) => {
    const expiryMs = row?.updated && row?.hours != null
        ? new Date(row.updated).getTime() + Number(row.hours) * 3600000
        : row?.expiresAt ? new Date(row.expiresAt).getTime() : NaN;
    if (Number.isNaN(expiryMs)) return { label: "—", tone: "none" };

    const diff = expiryMs - now;
    if (row?.isExpired) {
        return { label: diff < 0 ? `Expired ${formatDuration(-diff)} ago` : "Expired", tone: "expired" };
    }
    if (diff <= 0) return { label: "Expiring now", tone: "urgent" };
    return {
        label: `${formatDuration(diff)} left`,
        tone: diff <= 3600000 ? "urgent" : diff <= 6 * 3600000 ? "soon" : "ok",
    };
};

// same public url the case share modal generates (opens SharedDziView)
export const getShareLink = (shareid) => shareid ? `${window.location.origin}/shared-view/${shareid}` : "";

// diagnosis can come from the case editor as html - show it as plain text
export const toPlainText = (value = "") => {
    const div = document.createElement("div");
    div.innerHTML = value || "";
    return (div.textContent || div.innerText || "").trim();
};

// image cloudfront base url from site config (may not be loaded yet on first render)
export const useImageCloudfront = () => {
    const [imageCloudfront, setImageCloudfront] = useState(window.site?.common?.imageCloudfront || '');

    useEffect(() => {
        if (imageCloudfront) return;
        const timer = setTimeout(() => {
            if (window.site?.common?.imageCloudfront) {
                setImageCloudfront(window.site.common.imageCloudfront)
            }
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    return imageCloudfront;
};

// loads one share record by shareid; when the api has nothing usable
// (e.g. "This link has expired") falls back to the row passed in navigate state
export const useShareDetails = (shareid, fallbackData) => {
    const [shareData, setShareData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showSessionPopup, setShowSessionPopup] = useState(false);

    useEffect(() => {
        if (!shareid) return;
        let cancelled = false;
        setIsLoading(true);

        tmdbApi.getShareHistoryDetails(shareid)
            .then((response) => {
                if (cancelled) return;
                if (response?.result === "Invalid token or Expired") {
                    setShowSessionPopup(true);
                    return;
                }
                const result = response?.result;
                const record = Array.isArray(result)
                    ? result[0]
                    : Array.isArray(result?.data)
                        ? result.data[0]
                        : result && typeof result === "object"
                            ? result
                            : null;
                setShareData(record || fallbackData || null);
            })
            .catch((err) => {
                if (cancelled) return;
                console.log('error', err);
                setShareData(fallbackData || null);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => { cancelled = true; };
    }, [shareid]);

    return { shareData, isLoading, showSessionPopup };
};
